import React, { useState, useEffect } from 'react';
import { Col, Row, Text, Scroll, useDynamicResponsiveValue } from 'react-quick-style-components';
import { StyleSheet, useWindowDimensions, ActivityIndicator } from 'react-native';
import { COLOR, fSize } from 'const';
import Button from './Button';
import { VarHelper } from 'helpers';
import Store from 'store';

interface IEsignModalProps {
  visible: boolean;
  onClose: () => void;
  selectedCasks: Array<any>;
  clientEmail: string;
  clientName: string;
  onSignSuccess?: (caskIds: string[]) => void; // Callback when signature request sent successfully
  onSaveSuccess?: () => void; // Callback when draft saved successfully
}

const EsignModal = (props: IEsignModalProps) => {
  const { visible, onClose, selectedCasks, clientEmail, clientName, onSignSuccess, onSaveSuccess } = props;
  const { width, height } = useWindowDimensions();
  const rV = useDynamicResponsiveValue();
  const [loading, setLoading] = useState(false);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPath, setPdfPath] = useState<string | null>(null);
  const [, eApi] = Store.Esign.createStore();

  console.log('=== EsignModal render ===');
  console.log('visible:', visible);
  console.log('selectedCasks.length:', selectedCasks.length);
  console.log('clientEmail:', clientEmail);
  console.log('clientName:', clientName);

  const modalWidth = rV({ xs: width - 40, md: width * 0.9, lg: width * 0.85, xl: 1400 });
  const modalHeight = rV({ xs: height - 80, md: height * 0.85, lg: height * 0.9 });

  // Generate PDF preview when modal opens
  useEffect(() => {
    if (visible && selectedCasks.length > 0) {
      generatePdfPreview();
    } else {
      // Reset when modal closes
      setPdfPreviewUrl(null);
      setPdfPath(null);
    }
  }, [visible]);

  const generatePdfPreview = async () => {
    setGeneratingPreview(true);
    
    const caskIds = selectedCasks.map((cask) => {
      if (cask.id) return String(cask.id);
      console.warn('Cask missing id field, using Cask No:', cask);
      return cask['Cask No'];
    });
    
    try {
      const [res, err] = await eApi.generatePdfPreview({
        caskIds,
        clientEmail,
        clientName: clientName || clientEmail,
      });

      setGeneratingPreview(false);

      if (err) {
        console.error('Failed to generate PDF preview:', err);
        alert(`Error generating PDF preview: ${err}`);
        return;
      }

      if (res && res.success && res.data) {
        setPdfPreviewUrl(res.data.previewUrl);
        setPdfPath(res.data.previewPdfPath);
        console.log('PDF preview generated:', res.data.previewUrl);
      } else {
        console.error('API response:', res);
        alert(res?.message || 'Failed to generate PDF preview');
      }
    } catch (error) {
      setGeneratingPreview(false);
      console.error('Exception in generatePdfPreview:', error);
      alert(`Error: ${error.message || 'Failed to generate PDF preview'}`);
    }
  };

  const handleSaveDraft = async () => {
    if (selectedCasks.length === 0) {
      alert('Please select at least one cask');
      return;
    }

    if (!clientEmail) {
      alert('Client email is required');
      return;
    }

    setLoading(true);
    
    const caskIds = selectedCasks.map((cask) => {
      if (cask.id) return String(cask.id);
      console.warn('Cask missing id field, using Cask No:', cask);
      return cask['Cask No'];
    });
    
    const [res, err] = await eApi.saveDraft({
      caskIds,
      clientEmail,
      clientName: clientName || clientEmail,
      pdfPath,
    });

    setLoading(false);

    if (err) {
      alert(`Error: ${err}`);
      return;
    }

    if (res && res.success) {
      alert('Draft saved successfully!');
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      onClose();
    } else {
      alert(res?.message || 'Failed to save draft');
    }
  };

  const handleSend = async () => {
    if (selectedCasks.length === 0) {
      alert('Please select at least one cask');
      return;
    }

    if (!clientEmail) {
      alert('Client email is required');
      return;
    }

    setLoading(true);
    
    // Backend expects 'id' field from database
    const caskIds = selectedCasks.map((cask) => {
      if (cask.id) return String(cask.id);
      // Fallback to Cask No if id not available (shouldn't happen)
      console.warn('Cask missing id field, using Cask No:', cask);
      return cask['Cask No'];
    });
    
    const [res, err] = await eApi.sendSignatureRequest({
      caskIds,
      clientEmail,
      clientName: clientName || clientEmail,
    });

    setLoading(false);

    if (err) {
      alert(`Error: ${err}`);
      return;
    }

    if (res && res.success) {
      alert('Signature request sent successfully!');
      // Notify parent component about successful sign
      if (onSignSuccess) {
        onSignSuccess(caskIds);
      }
      onClose();
    } else {
      alert(res?.message || 'Failed to send signature request');
    }
  };

  if (!visible) return null;

  return (
    <Col
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
      flex1
      middle
      backgroundColor="rgba(0,0,0,0.5)"
      onPress={onClose}
    >
      <Col
        width={modalWidth}
        height={modalHeight}
        backgroundColor={COLOR.WHITE}
        borderRadius={10}
        onPress={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
          {/* Header - Fixed */}
          <Col padding20 paddingBottom10>
            <Row justifyContent="space-between" alignItems="center" marginBottom15>
              <Text fontSize={fSize(rV, 28)} bold color={COLOR.MAIN}>
                Cask Transfer Agreement
              </Text>
              <Col onPress={onClose} padding10 style={{ cursor: 'pointer' }}>
                <Text fontSize={24} color={COLOR.FONT}>×</Text>
              </Col>
            </Row>

            {/* Client Info */}
            <Col
              backgroundColor="rgba(173, 133, 85, 0.1)"
              padding15
              borderRadius={5}
            >
              <Row marginBottom5>
                <Text fontSize={15} bold color={COLOR.MAIN}>Client: </Text>
                <Text fontSize={15}>{clientName || 'N/A'}</Text>
              </Row>
              <Row>
                <Text fontSize={15} bold color={COLOR.MAIN}>Email: </Text>
                <Text fontSize={15}>{clientEmail || 'N/A'}</Text>
              </Row>
            </Col>
          </Col>

          {/* Main Content - Two Columns - Scrollable */}
          <Row 
            alignItems="stretch" 
            paddingHorizontal20
            style={{ 
              flex: 1, 
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            {/* Left Column - Cask List */}
            <Col flex={0.4} marginRight10 style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
              <Text fontSize={16} bold marginBottom10>
                Selected Casks ({selectedCasks.length})
              </Text>
              <Col style={{ flex: 1, overflow: 'auto', height: '100%' }}>
                <Col
                  borderWidth={1}
                  borderColor="rgba(0,0,0,0.1)"
                  borderRadius={5}
                >
                  {/* Table Header */}
                  <Row
                    backgroundColor={COLOR.MAIN}
                    paddingVertical10
                    paddingHorizontal10
                  >
                    <Col flex={1}>
                      <Text color={COLOR.WHITE} fontSize={12} bold>Cask NO</Text>
                    </Col>
                    <Col flex={1}>
                      <Text color={COLOR.WHITE} fontSize={12} bold>Spirit</Text>
                    </Col>
                    <Col flex={1}>
                      <Text color={COLOR.WHITE} fontSize={12} bold>Price</Text>
                    </Col>
                  </Row>

                  {/* Table Body */}
                  {selectedCasks.map((cask, index) => (
                    <Row
                      key={`cask-${index}`}
                      paddingVertical8
                      paddingHorizontal10
                      backgroundColor={index % 2 === 0 ? 'white' : 'rgba(0,0,0,0.02)'}
                      borderTopWidth={1}
                      borderColor="rgba(0,0,0,0.05)"
                    >
                      <Col flex={1}>
                        <Text fontSize={12}>{cask['Cask No'] || 'N/A'}</Text>
                      </Col>
                      <Col flex={1}>
                        <Text fontSize={12}>{cask['Spirit'] || 'N/A'}</Text>
                      </Col>
                      <Col flex={1}>
                        <Text fontSize={12}>{VarHelper.money(cask['Portfolio'])}</Text>
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Col>
            </Col>

            {/* Right Column - PDF Preview */}
            <Col flex={0.6} marginLeft10 style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
              <Text fontSize={16} bold marginBottom10>
                Document Preview
              </Text>
              <Col
                borderWidth={1}
                borderColor="rgba(0,0,0,0.1)"
                borderRadius={5}
                backgroundColor="rgba(0,0,0,0.02)"
                middle
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                {generatingPreview ? (
                  <Col middle>
                    <ActivityIndicator size="large" color={COLOR.MAIN} />
                    <Text marginTop10 fontSize={14}>Generating PDF preview...</Text>
                  </Col>
                ) : pdfPreviewUrl ? (
                  <iframe
                    src={pdfPreviewUrl}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 5,
                    }}
                    title="PDF Preview"
                  />
                ) : (
                  <Col middle>
                    <Text fontSize={14} color={COLOR.FONT}>
                      No preview available
                    </Text>
                    <Col
                      onPress={generatePdfPreview}
                      marginTop10
                      paddingHorizontal15
                      paddingVertical8
                      backgroundColor={COLOR.MAIN}
                      borderRadius={5}
                      style={{ cursor: 'pointer' }}
                    >
                      <Text color={COLOR.WHITE} fontSize={13}>
                        Generate Preview
                      </Text>
                    </Col>
                  </Col>
                )}
              </Col>
            </Col>
          </Row>

          {/* Footer - Fixed at bottom */}
          <Col 
            padding20 
            marginTop15 
            
            paddingBottom20
            style={{ 
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            {/* Info Text */}
            <Col
              backgroundColor="rgba(255, 193, 7, 0.1)"
              padding10
              borderRadius={5}
              marginBottom15
            >
              <Text fontSize={13} color={COLOR.FONT}>
                ℹ️ The client will receive an email with the Cask Transfer Agreement to sign via Dropbox Sign.
              </Text>
            </Col>

            {/* Action Buttons */}
            <Row justifyContent="space-between">
            {/* Left side - Cancel button */}
            <Col
              onPress={loading ? undefined : onClose}
              middle
              paddingHorizontal20
              paddingVertical8
              borderRadius={6}
              backgroundColor={COLOR.WHITE}
              borderWidth={1}
              borderColor="#D0D0D0"
              style={{
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
                minWidth: rV({ xs: 100, md: 110 }),
              }}
            >
              <Text 
                fontSize={fSize(rV, 18)} 
                color={COLOR.MAIN}
              >
                Cancel
              </Text>
            </Col>

            {/* Right side - Save Draft and Send buttons */}
            <Row>
              <Col
                onPress={loading || generatingPreview ? undefined : handleSaveDraft}
                middle
                paddingHorizontal20
                paddingVertical8
                borderRadius={6}
                backgroundColor={COLOR.WHITE}
                borderWidth={1}
                borderColor={COLOR.MAIN}
                marginRight12
                style={{
                  cursor: (loading || generatingPreview) ? 'not-allowed' : 'pointer',
                  opacity: (loading || generatingPreview) ? 0.5 : 1,
                  minWidth: rV({ xs: 120, md: 140 }),
                }}
              >
                <Text 
                  fontSize={fSize(rV, 18)} 
                  color={COLOR.MAIN}
                >
                  Save Draft
                </Text>
              </Col>
              
              <Col
                onPress={loading || generatingPreview ? undefined : handleSend}
                middle
                paddingHorizontal20
                paddingVertical8
                borderRadius={6}
                backgroundColor={(loading || generatingPreview) ? '#B8956B' : COLOR.MAIN}
                style={{
                  cursor: (loading || generatingPreview) ? 'not-allowed' : 'pointer',
                  minWidth: rV({ xs: 160, md: 180 }),
                }}
              >
                <Row middle gap={6}>
                  {loading && (
                    <ActivityIndicator size="small" color={COLOR.WHITE} />
                  )}
                  <Text 
                    fontSize={fSize(rV, 18)} 
                    color={COLOR.WHITE}
                  >
                    {loading ? 'Sending...' : 'Send for Signature'}
                  </Text>
                </Row>
              </Col>
            </Row>
            </Row>
          </Col>

          {loading && (
            <Col
              absoluteFill
              backgroundColor="rgba(255,255,255,0.8)"
              middle
              borderRadius={10}
            >
              <ActivityIndicator size="large" color={COLOR.MAIN} />
              <Text marginTop10 fontSize={16}>Sending request...</Text>
            </Col>
          )}
        </Col>
      </Col>
  );
};

const styles = StyleSheet.create({});

export default EsignModal;

